import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import useGetChainInfo from '@/hooks/useGetChainInfo';
import { maskAddressValue } from '@/utils/format';

import { EventTriggerType, HypurrNetPoolPositionsStrategy, Trigger } from '../../domain';
import { TriggerType } from './components/TriggerType';
import { TriggerValueDetail } from './components/TriggerValueDetail';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const HypurrNetPoolPositionsTriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const hyTokenAddress = useMemo(() => {
    return trigger.context?.hyTokenAddress || '';
  }, [trigger.context]);

  const variableDebtTokenAddress = useMemo(() => {
    return trigger.context?.variableDebtTokenAddress || '';
  }, [trigger.context]);

  const positionType = useMemo(() => {
    const strategy = trigger.context?.strategy;
    return strategy === HypurrNetPoolPositionsStrategy.Deposit
      ? 'Deposit'
      : strategy === HypurrNetPoolPositionsStrategy.Borrow
        ? 'Borrow'
        : '';
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    const chainId = trigger.context?.chainId;
    if (!chainId) return null;
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [trigger.context]);

  const chainIconUrl = useMemo(() => {
    if (!networkInfo) return '';
    return getChainIconUrl(networkInfo.chainId) || '';
  }, [networkInfo, getChainIconUrl]);

  const pairDisplay = useMemo(() => {
    if (!hyTokenAddress || !variableDebtTokenAddress) return '';

    return (
      <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
        {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={networkInfo?.name || 'chain'} />}
        <span>
          {maskAddressValue(hyTokenAddress)} - {maskAddressValue(variableDebtTokenAddress)}
        </span>
      </div>
    );
  }, [hyTokenAddress, variableDebtTokenAddress, chainIconUrl, networkInfo]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <TriggerType eventType={triggerType} />

      <TriggerValueDetail label="Pair" value={pairDisplay} />

      <TriggerValueDetail label="Position Type" value={positionType} />
    </div>
  );
};
